/**
 * Downscale a base64/data-URL image to fit within maxSize (longest side) and return a JPEG data
 * URL. Used to shrink a full-res 3D screenshot into an inline blueprint thumbnail that embeds in
 * the blueprint JSON without bloating it. Falls back to the original src on failure.
 *
 * JPEG, not WebP: this image is consumed by @react-pdf (estimate/proposal PDFs), which decodes
 * ONLY JPEG and PNG, and by HubSpot. A WebP thumbnail silently renders as nothing in the PDF.
 */
export const downscaleImage = async (src: string, maxSize = 1024, quality = 0.82): Promise<string> => {
	if (!src || !src.startsWith('data:')) return src;
	try {
		const img = await new Promise<HTMLImageElement>((resolve, reject) => {
			const el = new Image();
			el.onload = () => resolve(el);
			el.onerror = () => reject(new Error('downscaleImage: load failed'));
			el.src = src;
		});

		const { naturalWidth: width, naturalHeight: height } = img;
		const scale = Math.min(1, maxSize / Math.max(width, height));

		// Already at target and already JPEG — re-encoding would stack generational loss (the
		// canonical raw/ai toggle sends a previously downscaled image straight back through).
		if (scale === 1 && src.startsWith('data:image/jpeg')) return src;

		const canvas = document.createElement('canvas');
		canvas.width = Math.max(1, Math.round(width * scale));
		canvas.height = Math.max(1, Math.round(height * scale));

		const ctx = canvas.getContext('2d');
		if (!ctx) return src;
		ctx.fillStyle = '#FFFFFF';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

		return canvas.toDataURL('image/jpeg', quality);
	} catch {
		return src;
	}
};
